/* eslint-disable camelcase */
import React, { useState, useCallback, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { Form, Formik } from 'formik';

import { createLocale } from 'locales';

import TextField from 'form-elements/text-field';
import SelectField from 'form-elements/select-field';

import Button from 'components-new/button/button';
import { showErrorToast, showSuccessToast } from 'components-new/toast/toast';
import Typography from 'components-new/typography/typography';
import Divider from 'components-new/divider/divider';

import Icon from 'components/icon/icon';

import { STATIC_FILES_URL } from 'constants/signup-flow-constants';

import type { Address } from 'state/__generated__/tadaahAPI';
import useUpdateFreelancerKvkInformation from 'state/remote/remote-freelancer/use-update-freelancer-kvk-information';

import freelancerRoutes from '../freelancer-routes';

import FreelancerSignUpPageContainer from '../components/freelancer-sign-up-page-container';

import { KVKNumberSchema, KVKDataSchema } from './schema';

const localize = createLocale('freelancerKVKInformationPageLocales');
const globalLocalize = createLocale('global');

const videoUrl = `${STATIC_FILES_URL}videos/signupflow/h3.mp4`;
const videoPreviewImageURL = `${window.STATIC_URL}assets/signupflow/h3.png`;

interface KVKNumberFormValues {
  kvk_nummer: string;
}

interface CompanyDataFormValues {
  address: string | null | undefined;
  sbi_codes: string | null | undefined;
  company_name: string | null | undefined;
}

const KVKInformation: React.FC = () => {
  const navigate = useNavigate();
  const [KVKNumber, setKVKNumber] = useState<string>('');
  const [informationIncorrectClicked, setInformationIncorrectClicked] =
    useState<boolean>(false);

  const {
    addressOptions,
    companyNameOptions,
    error,
    initialValues,
    isSuccess,
    isLoading,
    KVKData,
    KVKError,
    KVKIsLoading,
    KVKIsSuccess,
    KVKRefetch,
    updateFreelancerKvkInformation,
  } = useUpdateFreelancerKvkInformation({ kvk_number: KVKNumber }, !!KVKNumber);

  const getAddressFromRaw = useCallback(
    (raw_address: string) => {
      const kvk_address = KVKData?.addresses?.find(
        (address) => address.raw_address === raw_address,
      );
      return {
        address_type: 'Work' as const,
        city: kvk_address?.city,
        house_number: kvk_address?.house_number,
        house_number_extension: kvk_address?.house_number_extension,
        postal_code: kvk_address?.postal_code,
        raw_address,
        street_name: kvk_address?.street_name || raw_address,
      } as Address;
    },
    [KVKData?.addresses],
  );

  const navigateToDocumentsPage = useCallback(() => {
    navigate(freelancerRoutes.documentsInformation.path);
  }, [navigate]);

  const onKVKNumberSubmitted = useCallback(
    (values: KVKNumberFormValues) => {
      setKVKNumber(values.kvk_nummer);
      KVKRefetch();
    },
    [KVKRefetch],
  );

  const onCompanyDataSubmitted = useCallback(
    (values: CompanyDataFormValues) => {
      const address = getAddressFromRaw(values.address as string);
      updateFreelancerKvkInformation({
        ...values,
        address,
        kvk_nummer: KVKNumber,
      });
    },
    [KVKNumber, getAddressFromRaw, updateFreelancerKvkInformation],
  );

  const onSkipButtonClicked = useCallback(() => {
    navigateToDocumentsPage();
  }, [navigateToDocumentsPage]);

  const onInformationIncorrectClicked = useCallback(() => {
    setInformationIncorrectClicked((prevState) => !prevState);
  }, []);

  const KVKDataIncomplete =
    KVKData &&
    (!KVKData.addresses ||
      !KVKData.addresses.length ||
      !KVKData.company_names.length ||
      !KVKData.sbi_codes.length);

  const KVKInformationAlreadySaved =
    initialValues.address &&
    initialValues.company_name &&
    initialValues.kvk_nummer;

  useEffect(() => {
    if (isSuccess) {
      showSuccessToast({
        description: localize('companyDataSaved'),
        title: globalLocalize('notification'),
      });
      navigateToDocumentsPage();
    } else if (error) {
      const errorDescription = error.response?.data
        ? JSON.stringify(error.response?.data)
        : error.message;
      showErrorToast({
        description: errorDescription,
        title: globalLocalize('error'),
      });
    } else if (KVKIsSuccess) {
      showSuccessToast({
        description: localize('kvkInformationSuccessfullyFetched'),
        title: globalLocalize('notification'),
      });
    } else if (KVKError) {
      if (KVKError?.response?.status === 429) {
        showErrorToast({
          description: localize('errorTooManyRequests'),
          title: globalLocalize('error'),
        });
      } else if (
        KVKError.response?.status === 400 &&
        KVKError.response?.data.error.startsWith(
          'Er is al een account gekoppeld aan dit kvk-nummer.',
        )
      ) {
        showErrorToast({
          description: localize('errorKVKAlreadyExists'),
          title: globalLocalize('error'),
        });
      } else {
        showErrorToast({
          description: localize('errorFetchingKVKInformation'),
          title: globalLocalize('error'),
        });
      }
    }
  }, [
    isSuccess,
    error,
    KVKIsSuccess,
    KVKError,
    navigateToDocumentsPage,
    KVKData,
  ]);

  return (
    <FreelancerSignUpPageContainer
      showHeader
      showGoBackButton
      headerTitle={localize('title')}
      dialogInstructions={{
        component: (
          <>
            <Typography
              as="p"
              variant="body-medium"
              className="text-gray600 whitespace-pre-line"
            >
              {localize('kvkInformationDialogdialogFirstInstruction')}
            </Typography>
            <Typography as="span" variant="body-medium">
              <a href="https://kvk.nl">https://www.kvk.nl</a>
            </Typography>
          </>
        ),
        shouldShowDialogInstructions: !KVKInformationAlreadySaved,
      }}
      videoInstructions={{
        url: videoUrl,
        videoPreviewImageURL,
      }}
      onGoBackClick={() => navigate(freelancerRoutes.fillInformation.path)}
      className="flex flex-col items-center"
    >
      <Typography as="p" variant="body-medium" className="text-gray500 mt-6">
        {localize('enterKVKNumberMessage')}
      </Typography>
      <Formik
        validationSchema={KVKNumberSchema}
        initialValues={{
          kvk_nummer: initialValues.kvk_nummer || '',
        }}
        onSubmit={onKVKNumberSubmitted}
        enableReinitialize
      >
        {({ values }) => (
          <Form className="w-full">
            <TextField
              name="kvk_nummer"
              type="text"
              className="mt-4"
              title={localize('kvkNumber')}
              placeholder="12345678"
            />
            {initialValues.kvk_nummer || (KVKNumber && KVKIsSuccess) ? (
              <>
                {KVKNumber !== values.kvk_nummer && (
                  <Button
                    type="submit"
                    label={localize('refetchData')}
                    variant="secondary"
                    className="w-full justify-center mt-4"
                    loading={KVKIsLoading}
                    disabled={KVKIsLoading}
                  />
                )}

                <Divider orientation="horizontal" className="my-4 px-4" />
              </>
            ) : (
              <>
                <Button
                  type="submit"
                  label={globalLocalize('nextStep')}
                  variant="primary"
                  className="w-full justify-center mt-4"
                  loading={KVKIsLoading}
                  disabled={KVKIsLoading}
                />
                <Button
                  label={globalLocalize('skipStep')}
                  variant="secondary"
                  className="w-full justify-center mt-4"
                  onClick={onSkipButtonClicked}
                />
              </>
            )}
          </Form>
        )}
      </Formik>

      {KVKInformationAlreadySaved || (KVKData && !KVKError && KVKNumber) ? (
        <>
          {KVKDataIncomplete ? (
            <Typography variant="body-medium" as="p" className="text-red mb-3">
              {localize('kvkInformationIncomplete')}
            </Typography>
          ) : (
            <Typography
              as="p"
              variant="body-medium"
              className="text-gray500 mb-3"
            >
              {localize('checkKVKInformation')}
            </Typography>
          )}

          <Formik
            validationSchema={KVKDataSchema}
            initialValues={{
              address: KVKData?.addresses?.length
                ? KVKData.addresses[0].raw_address
                : initialValues.address?.raw_address,
              company_name: KVKData?.company_names.length
                ? KVKData.company_names[0]
                : initialValues.company_name,
              sbi_codes: KVKData?.sbi_codes
                ? KVKData?.sbi_codes.join(',')
                : initialValues.sbi_codes,
            }}
            onSubmit={onCompanyDataSubmitted}
            enableReinitialize
          >
            <Form className="mb-16 w-full">
              <SelectField
                name="company_name"
                type="text"
                className="mt-4"
                options={companyNameOptions()}
                title={localize('tradeName')}
              />
              <SelectField
                name="address"
                type="text"
                className="mt-4"
                options={addressOptions()}
                title={localize('registrationAddress')}
              />
              <TextField
                name="sbi_codes"
                type="text"
                className="mt-4"
                title={localize('sbiCodes')}
                disabled
              />
              <button
                type="button"
                onClick={onInformationIncorrectClicked}
                className="my-3 cursor-pointer self-start"
              >
                <Typography
                  variant="body-medium-bold"
                  as="p"
                  className=" mb-1 text-start"
                >
                  {localize('isInformationIncorrect')}
                  {informationIncorrectClicked ? (
                    <Icon size={14} icon="caret-down" />
                  ) : (
                    <Icon size={14} icon="caret-right" />
                  )}
                </Typography>
                {informationIncorrectClicked && (
                  <Typography
                    variant="body-medium"
                    as="p"
                    className="text-start"
                  >
                    {localize('informationIncorrectInfo')}
                  </Typography>
                )}
              </button>
              {/* if entered information is not the same as in database */}
              <Button
                type="submit"
                label={globalLocalize('saveAndContinue')}
                variant="primary"
                className="w-full justify-center mt-4"
                loading={isLoading}
                disabled={isLoading || KVKDataIncomplete}
              />
              <Button
                label={globalLocalize('skipStep')}
                variant="secondary"
                className="w-full justify-center mt-4"
                onClick={onSkipButtonClicked}
              />
            </Form>
          </Formik>
        </>
      ) : null}
    </FreelancerSignUpPageContainer>
  );
};

export default KVKInformation;
